import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  fetchOrgProjects,
  fetchProjectMembers,
  fetchUsers
} from '@/features/users/usersSlice'
import {
  selectMembersCache,
  selectMembersError,
  selectMembersItems,
  selectProjectsError,
  selectProjectsForSelector,
  selectSessionUserIdHex
} from '@/features/users/usersSelectors'
import { formatDate, initialsFor } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store'

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/**
 * The members browser (PX-W6.2, AC-3): one project's roster with each row's
 * server-declared `side` (`side ?? 'FIRM'`, mirroring
 * `ProjectMember.getSideOrFirm`), `role` and `accessLevel` rendered exactly as
 * the server wrote them.
 *
 * The inheritance ceiling (onboarding step 05) is DISPLAYED from the caller's
 * OWN row in the same response — never recomputed from a role table (INV-6:
 * no client module maps a role to an access level). A caller with no member
 * row renders no chip: a stated absence.
 */
export function MembersBrowser() {
  const { t } = useTranslation('peopleAdmin')
  const dispatch = useAppDispatch()

  const projects = useAppSelector(selectProjectsForSelector)
  const projectsError = useAppSelector(selectProjectsError)
  const items = useAppSelector(selectMembersItems)
  const membersError = useAppSelector(selectMembersError)
  const membersCache = useAppSelector(selectMembersCache)
  const sessionUserId = useAppSelector((s) => s.session.user?.id ?? null)
  const sessionUserHex = useAppSelector((s) => selectSessionUserIdHex(s, sessionUserId))

  const [projectId, setProjectId] = useState('')

  // The project selector and the session user's hex mapping both read the
  // users/projects directories — org reads every member may perform.
  useEffect(() => {
    void dispatch(fetchOrgProjects())
    void dispatch(fetchUsers())
  }, [dispatch])

  useEffect(() => {
    if (projectId !== '') void dispatch(fetchProjectMembers(projectId))
  }, [projectId, dispatch])

  // The caller's own row — the ceiling the SERVER already granted them.
  const mine =
    sessionUserHex === null ? undefined : items.find((row) => row.userId === sessionUserHex)

  const onProjectChanged = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setProjectId(event.target.value)
  }

  return (
    <section
      aria-labelledby="members-browser-label"
      className="rounded-lg border border-border bg-card px-4 py-4"
    >
      <div id="members-browser-label" className="flex flex-wrap items-center gap-3">
        <SectionLabel>{t('members.title')}</SectionLabel>
        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          {t('members.project')}
          <select
            value={projectId}
            onChange={onProjectChanged}
            className="max-w-72 rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
          >
            <option value="">{t('members.projectNone')}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name ?? project.id}
              </option>
            ))}
          </select>
        </label>
        {mine !== undefined && mine.accessLevel !== undefined && (
          <span data-testid="members-ceiling">
            <StatusPill tone="decided">
              {t('members.ceiling', { level: mine.accessLevel })}
            </StatusPill>
          </span>
        )}
      </div>

      <div className="mt-3">
        {projectsError !== null && projectsError.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/projects" />
        ) : projectId === '' ? (
          <EmptyPanel body={t('members.noProject')} />
        ) : membersCache.loading ? (
          <LoadingPanel />
        ) : membersError !== null && membersError.status === 403 ? (
          <ForbiddenPanel requested={`/{orgId}/projects/${projectId}/members`} />
        ) : membersError !== null ? (
          <ErrorPanel
            error={membersError}
            onRetryClicked={() => void dispatch(fetchProjectMembers(projectId))}
          />
        ) : items.length === 0 ? (
          <EmptyPanel body={t('members.empty')} />
        ) : (
          <Table>
            <TableCaption className="sr-only">{t('members.title')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className={columnHeadClass}>{t('members.columns.person')}</TableHead>
                <TableHead className={columnHeadClass}>{t('members.columns.role')}</TableHead>
                <TableHead className={columnHeadClass}>{t('members.columns.access')}</TableHead>
                <TableHead className={columnHeadClass}>{t('members.columns.side')}</TableHead>
                <TableHead className={columnHeadClass}>{t('members.columns.added')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row, index) => {
                const side = row.side ?? 'FIRM'
                return (
                  <TableRow key={row.id ?? row.userId ?? index}>
                    <TableCell className="py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span
                          aria-hidden
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-people-bg text-xs font-semibold text-people-ink"
                        >
                          {row.initials ?? initialsFor(row.name)}
                        </span>
                        <span className="font-medium text-foreground">{row.name ?? '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-ink-soft">{row.role ?? '—'}</TableCell>
                    <TableCell>
                      {row.accessLevel !== undefined ? (
                        <StatusPill tone="proposed">{row.accessLevel}</StatusPill>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusPill tone={side === 'CLIENT' ? 'people' : 'neutral'}>
                        {side === 'CLIENT' ? t('members.sideClient') : t('members.sideFirm')}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums text-foreground">
                      {formatDate(row.addedAt) ?? '—'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}
